import React from "react";
import useSWR, { mutate } from "swr";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/app/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ROLES, roleLabel, contractLabel, CONTRACT_TYPES, MARITAL_STATUS, CURRENCIES } from "@/lib/labels";
import { canViewPayroll } from "@/lib/permissions";
import { DatePicker } from "@/components/app/DatePicker";
import { toast } from "sonner";
import { UserPlus, PencilSimple, Trash, Prohibit, CheckCircle } from "@phosphor-icons/react";

const fetcher = (u) => api.get(u).then((r) => r.data);

const EMPTY = {
  email: "", full_name: "", password: "", role: "collab_fr", location: "France",
  matricule: "", position: "", department: "", hire_date: "", contract_type: "",
  gross_salary: "", salary_currency: "XAF", social_id: "", marital_status: "",
  children_count: "", birth_date: "", nationality: "", bank_name: "", bank_iban: "",
};

const buildPayload = (form, isEdit) => {
  const p = {};
  const strFields = ["full_name", "role", "location", "matricule", "position", "department",
    "hire_date", "contract_type", "salary_currency", "social_id", "marital_status",
    "birth_date", "nationality", "bank_name", "bank_iban"];
  strFields.forEach((f) => { if (form[f] !== "" && form[f] != null) p[f] = form[f]; });
  if (!isEdit) { p.email = form.email; p.password = form.password; }
  if (form.gross_salary !== "") p.gross_salary = parseFloat(form.gross_salary);
  if (form.children_count !== "") p.children_count = parseInt(form.children_count, 10);
  return p;
};

function Field({ label, tid, type = "text", field, form, setForm, ...rest }) {
  return (
    <div>
      <Label>{label}</Label>
      {type === "date" ? (
        <DatePicker value={form[field]} onChange={(v) => setForm({ ...form, [field]: v })} testid={tid} />
      ) : (
        <Input type={type} value={form[field]} data-testid={tid}
          onChange={(e) => setForm({ ...form, [field]: e.target.value })} {...rest} />
      )}
    </div>
  );
}

export default function Collaborators() {
  const { user: me } = useAuth();
  const { data: users = [] } = useSWR("/users", fetcher);
  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [form, setForm] = React.useState(EMPTY);
  const showPayroll = canViewPayroll(me?.role);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({
      ...EMPTY,
      email: u.email || "", full_name: u.full_name || "", role: u.role, location: u.location,
      matricule: u.matricule || "", position: u.position || "", department: u.department || "",
      hire_date: u.hire_date || "", contract_type: u.contract_type || "",
      gross_salary: u.gross_salary ?? "", salary_currency: u.salary_currency || "XAF",
      social_id: u.social_id || "", marital_status: u.marital_status || "",
      children_count: u.children_count ?? "", birth_date: u.birth_date || "",
      nationality: u.nationality || "", bank_name: u.bank_name || "", bank_iban: u.bank_iban || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/users/${editing.id}`, buildPayload(form, true));
        toast.success("Collaborateur mis à jour");
      } else {
        await api.post("/auth/register", buildPayload(form, false));
        toast.success("Collaborateur créé");
      }
      setOpen(false);
      mutate("/users");
    } catch (err) { toast.error(err?.response?.data?.detail || "Enregistrement impossible"); }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? "Compte désactivé" : "Compte réactivé");
      mutate("/users");
    } catch (err) { toast.error(err?.response?.data?.detail || "Action impossible"); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/users/${deleteTarget.id}`);
      toast.success("Collaborateur supprimé");
      setDeleteTarget(null);
      mutate("/users");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Suppression impossible");
      setDeleteTarget(null);
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Équipe" title="Collaborateurs"
        description="France & Cameroun — rôles, accès aux modules et données de paie."
        actions={
          <Button data-testid="new-collab-btn" onClick={openCreate} className="bg-[#2E3192] hover:bg-[#1E2066]">
            <UserPlus size={16} className="mr-1" /> Ajouter
          </Button>
        }
      />

      <div className="border border-[#E2E4F0] bg-white rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="collab-table">
          <thead className="bg-[#F8F8FC] border-b border-[#E2E4F0]">
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[#64748B]">
              <th className="px-4 py-3">Matricule</th>
              <th className="px-4 py-3">Nom</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Rôle</th>
              <th className="px-4 py-3">Poste</th>
              <th className="px-4 py-3">Localisation</th>
              <th className="px-4 py-3">Statut</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-[#F7F5FC]/60">
                <td className="px-4 py-3 font-mono text-xs">{u.matricule || "—"}</td>
                <td className="px-4 py-3 font-medium">{u.full_name}</td>
                <td className="px-4 py-3">{u.email}</td>
                <td className="px-4 py-3">{roleLabel(u.role)}</td>
                <td className="px-4 py-3">{u.position || "—"}{u.contract_type ? ` (${contractLabel(u.contract_type)})` : ""}</td>
                <td className="px-4 py-3">{u.location}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${u.active !== false ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`}
                    data-testid={`user-status-${u.id}`}>
                    {u.active !== false ? "Actif" : "Désactivé"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" title="Modifier" data-testid={`edit-user-btn-${u.id}`} onClick={() => openEdit(u)}>
                      <PencilSimple size={15} />
                    </Button>
                    <Button size="sm" variant="ghost" title={u.active !== false ? "Désactiver" : "Réactiver"}
                      data-testid={`toggle-active-btn-${u.id}`} onClick={() => toggleActive(u)}>
                      {u.active !== false ? <Prohibit size={15} className="text-amber-600" /> : <CheckCircle size={15} className="text-emerald-600" />}
                    </Button>
                    <Button size="sm" variant="ghost" title="Supprimer" data-testid={`delete-user-btn-${u.id}`} onClick={() => setDeleteTarget(u)}>
                      <Trash size={15} className="text-rose-600" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Dialog création / édition */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl" data-testid="collab-form-title">
              {editing ? `Modifier — ${editing.full_name}` : "Nouveau collaborateur"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-5">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-[#2E3192] font-semibold mb-2">Compte & accès</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field form={form} setForm={setForm} label="Nom complet" field="full_name" tid="collab-name" required />
                <Field form={form} setForm={setForm} label="Email" field="email" tid="collab-email" type="email" required={!editing} disabled={!!editing} />
                {!editing && <Field form={form} setForm={setForm} label="Mot de passe temporaire" field="password" tid="collab-password" required minLength={6} />}
                <div>
                  <Label>Rôle (accès aux modules)</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="collab-role"><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map((r) => (<SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Localisation</Label>
                  <Select value={form.location} onValueChange={(v) => setForm({ ...form, location: v })}>
                    <SelectTrigger data-testid="collab-location"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="France">France</SelectItem><SelectItem value="Cameroun">Cameroun</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {showPayroll && (
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-[#2E3192] font-semibold mb-2">Paie & RH (confidentiel)</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field form={form} setForm={setForm} label="Matricule" field="matricule" tid="collab-matricule" placeholder="JNE-001" />
                  <Field form={form} setForm={setForm} label="Poste / fonction" field="position" tid="collab-position" />
                  <Field form={form} setForm={setForm} label="Service" field="department" tid="collab-department" />
                  <Field form={form} setForm={setForm} label="Date d'embauche" field="hire_date" tid="collab-hire-date" type="date" />
                  <div>
                    <Label>Type de contrat</Label>
                    <Select value={form.contract_type} onValueChange={(v) => setForm({ ...form, contract_type: v })}>
                      <SelectTrigger data-testid="collab-contract-type"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{CONTRACT_TYPES.map((c) => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <Field form={form} setForm={setForm} label="Salaire brut mensuel" field="gross_salary" tid="collab-gross-salary" type="number" min="0" step="0.01" />
                  <div>
                    <Label>Devise</Label>
                    <Select value={form.salary_currency} onValueChange={(v) => setForm({ ...form, salary_currency: v })}>
                      <SelectTrigger data-testid="collab-salary-currency"><SelectValue /></SelectTrigger>
                      <SelectContent>{CURRENCIES.map((c) => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <Field form={form} setForm={setForm} label="N° CNPS / N° sécurité sociale" field="social_id" tid="collab-social-id" />
                  <div>
                    <Label>Situation familiale</Label>
                    <Select value={form.marital_status} onValueChange={(v) => setForm({ ...form, marital_status: v })}>
                      <SelectTrigger data-testid="collab-marital-status"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{MARITAL_STATUS.map((m) => (<SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <Field form={form} setForm={setForm} label="Nombre d'enfants" field="children_count" tid="collab-children-count" type="number" min="0" />
                  <Field form={form} setForm={setForm} label="Date de naissance" field="birth_date" tid="collab-birth-date" type="date" />
                  <Field form={form} setForm={setForm} label="Nationalité" field="nationality" tid="collab-nationality" />
                  <Field form={form} setForm={setForm} label="Banque" field="bank_name" tid="collab-bank-name" />
                  <Field form={form} setForm={setForm} label="RIB / IBAN" field="bank_iban" tid="collab-bank-iban" />
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="collab-cancel">Annuler</Button>
              <Button type="submit" className="bg-[#2E3192] hover:bg-[#1E2066]" data-testid="collab-submit">
                {editing ? "Enregistrer" : "Créer"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirmation suppression */}
      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">Supprimer ce collaborateur ?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#64748B]" data-testid="delete-confirm-text">
            {deleteTarget?.full_name} ({deleteTarget?.email}) sera définitivement supprimé.
            Si des temps, missions ou documents lui sont liés, la suppression sera refusée — désactivez-le plutôt.
          </p>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} data-testid="cancel-delete-btn">Annuler</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={confirmDelete} data-testid="confirm-delete-btn">Supprimer</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
